import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import './App.css';
import { SERVICES } from './services';
import ServiceDetail from './ServiceDetail';

function CountUp({ end, suffix = '', duration = 2000, active }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let rafId;
    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setVal(Math.round(progress * end));
      if (progress < 1) { rafId = requestAnimationFrame(animate); }
    };
    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [active, end, duration]);
  return <>{val}{suffix}</>;
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.4 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
    <Routes>
      <Route path="/service/:slug" element={<ServiceDetail />} />
      <Route path="/" element={
    <div className="tc-app">

      {/* â”€â”€ NAVBAR â”€â”€ */}
      <nav className="tc-navbar">
        <div className="tc-navbar__inner">
          <div className="tc-navbar__brand">
            <span className="tc-navbar__icon">
              {/* 24hrs Logo Badge */}
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <text x="14" y="13" textAnchor="middle" fill="white" fontSize="11" fontWeight="900" fontFamily="Arial,sans-serif">24</text>
                <text x="14" y="23" textAnchor="middle" fill="rgba(255,255,255,0.82)" fontSize="6.5" fontWeight="700" fontFamily="Arial,sans-serif" letterSpacing="1.5">HRS</text>
              </svg>
            </span>
            <span className="tc-navbar__name">24hrs-<span className="tc-yellow">Taxicabs</span></span>
          </div>
          <button className="tc-navbar__toggle" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
            <i className={menuOpen ? 'fas fa-times' : 'fas fa-bars'}></i>
          </button>
          <ul className={`tc-navbar__links ${menuOpen ? 'tc-navbar__links--open' : ''}`}>
            <li><a href="#home" onClick={() => setMenuOpen(false)}>Home</a></li>
            <li><a href="#services" onClick={() => setMenuOpen(false)}>Services</a></li>
            <li><a href="#airport" onClick={() => setMenuOpen(false)}>Airport Transfers</a></li>
            <li><a href="#about" onClick={() => setMenuOpen(false)}>About</a></li>
            <li><a href="#contact" onClick={() => setMenuOpen(false)}>Contact</a></li>
            <li className="tc-navbar__book-item"><a href="https://booktaxinow.co.uk/" target="_blank" rel="noreferrer" className="tc-navbar__book-btn" onClick={() => setMenuOpen(false)}>Book Now</a></li>
          </ul>
        </div>
      </nav>

      {/* â”€â”€ HERO â”€â”€ */}
      <section className="tc-hero" id="home" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/Airport-Transfers-to-Perth-Airport.jpg)` }}>
        <div className="tc-hero__overlay"></div>
        <div className="tc-hero__content">
          <p className="tc-hero__eyebrow tc-hero__eyebrow--desktop"><i className="fas fa-plane-departure tc-yellow-txt"></i> Airport Transfers &bull; Local Rides &bull; Long Distance</p>
          <h1 className="tc-hero__heading">24hrs-Taxicabs<br /><span className="tc-yellow">Always Ready to Drive</span></h1>
          <p className="tc-hero__sub">From airport pickups to everyday travel, 24hrs-Taxicabs provides safe, reliable, and affordable transportation with experienced drivers and comfortable vehicles.</p>
          <div className="tc-hero__actions">
            <a href="tel:02038393300" className="tc-action-tile tc-action-tile--orange">
              <span className="tc-action-tile__icon"><i className="fas fa-phone-alt"></i></span>
              <span className="tc-action-tile__body">
                <span className="tc-action-tile__label">Call Now</span>
                <span className="tc-action-tile__sub">Available 24 / 7</span>
              </span>
            </a>
            <a href="https://booktaxinow.co.uk/" target="_blank" rel="noreferrer" className="tc-action-tile tc-action-tile--violet">
              <span className="tc-action-tile__icon"><i className="fas fa-car"></i></span>
              <span className="tc-action-tile__body">
                <span className="tc-action-tile__label">Book Now</span>
                <span className="tc-action-tile__sub">Instant Confirmation</span>
              </span>
            </a>
            <a href="https://booktaxinow.co.uk/" target="_blank" rel="noreferrer" className="tc-action-tile tc-action-tile--green">
              <span className="tc-action-tile__icon"><i className="fas fa-file-invoice-dollar"></i></span>
              <span className="tc-action-tile__body">
                <span className="tc-action-tile__label">Get Quotation</span>
                <span className="tc-action-tile__sub">Free & No Obligation</span>
              </span>
            </a>
            <a href="https://booktaxinow.co.uk/" target="_blank" rel="noreferrer" className="tc-action-tile tc-action-tile--sky">
              <span className="tc-action-tile__icon"><i className="fas fa-plane-departure"></i></span>
              <span className="tc-action-tile__body">
                <span className="tc-action-tile__label">Airport Booking</span>
                <span className="tc-action-tile__sub">Flight Tracking Included</span>
              </span>
            </a>
          </div>
          <div className="tc-hero__badges">
            <div className="tc-badge"><i className="fas fa-shield-alt"></i><span>Safe &amp; Insured</span></div>
            <div className="tc-badge"><i className="fas fa-clock"></i><span>On-Time Guarantee</span></div>
            <div className="tc-badge"><i className="fas fa-star"></i><span>4.9â˜… Rated</span></div>
          </div>
        </div>
      </section>

      {/* â”€â”€ STATS â”€â”€ */}
      <section className="tc-stats" ref={statsRef}>
        <div className="tc-container tc-stats__grid">
          <div className="tc-stat">
            <div className="tc-stat__icon"><i className="fas fa-users"></i></div>
            <span className="tc-stat__num"><CountUp end={15} suffix="K+" active={statsVisible} /></span>
            <span className="tc-stat__label">Happy Riders</span>
          </div>
          <div className="tc-stat">
            <div className="tc-stat__icon"><i className="fas fa-car"></i></div>
            <span className="tc-stat__num"><CountUp end={200} suffix="+" active={statsVisible} /></span>
            <span className="tc-stat__label">Vehicles</span>
          </div>
          <div className="tc-stat">
            <div className="tc-stat__icon"><i className="fas fa-calendar-alt"></i></div>
            <span className="tc-stat__num"><CountUp end={10} suffix="+" active={statsVisible} /></span>
            <span className="tc-stat__label">Years of Service</span>
          </div>
          <div className="tc-stat">
            <div className="tc-stat__icon"><i className="fas fa-clock"></i></div>
            <span className="tc-stat__num">24/7</span>
            <span className="tc-stat__label">Availability</span>
          </div>
        </div>
      </section>

      {/* â”€â”€ SERVICES â”€â”€ */}
      <section className="tc-section" id="services">
        <div className="tc-container">
          <div className="tc-section__header">
            <p className="tc-section__eyebrow">What We Offer</p>
            <h2 className="tc-section__title">Our <span className="tc-yellow">Services</span></h2>
            <p className="tc-section__desc">From airport pickups to everyday travel, we have a solution for every journey.</p>
          </div>
          <div className="tc-srv-grid">
            {SERVICES.map((s) => (
              <div className="tc-srv-card" key={s.slug}>
                <div className="tc-srv-card__img">
                  <img src={`${process.env.PUBLIC_URL}${s.img}`} alt={s.title} />
                </div>
                <div className="tc-srv-card__body">
                  <div className="tc-srv-card__icon-wrap"><i className={s.icon}></i></div>
                  <h3 className="tc-srv-card__title">{s.title}</h3>
                  <p className="tc-srv-card__desc">{s.desc}</p>
                  <div className="tc-srv-card__btns">
                    <Link to={`/service/${s.slug}`} className="tc-srv-card__btn tc-srv-card__btn--read">Read More</Link>
                    <a href="https://booktaxinow.co.uk/" target="_blank" rel="noreferrer" className="tc-srv-card__btn tc-srv-card__btn--book">Book Now</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* â”€â”€ AIRPORT TRANSFERS â”€â”€ */}
      <section className="tc-section tc-section--dark" id="airport">
        <div className="tc-container">
          <div className="tc-section__header">
            <p className="tc-section__eyebrow tc-yellow"><i className="fas fa-plane-departure"></i> &nbsp;Airport Transfers</p>
            <h2 className="tc-section__title">We Cover All <span className="tc-yellow">Major UK Airports</span></h2>
            <p className="tc-section__desc">From Heathrow to Edinburgh, our professional drivers deliver punctual, comfortable airport transfers across the UK â€” with real-time flight tracking and no hidden fees.</p>
          </div>
          <div className="tc-airports-list">
            {[
              { code: 'LHR', name: 'Heathrow Airport', desc: '24hrs-Taxicabs provides reliable and professional transfers to and from Heathrow Airport, ensuring every journey is comfortable and stress-free. Our experienced drivers offer punctual pickups, flight monitoring, and assistance with luggage for a seamless travel experience. Whether you\'re travelling for business or leisure, 24hrs-Taxicabs guarantees safe, comfortable, and on-time airport transportation.' },
              { code: 'LGW', name: 'Gatwick Airport', desc: '24hrs-Taxicabs offers dependable airport transfer services to and from Gatwick Airport with a focus on comfort, reliability, and punctuality. Our professional drivers ensure timely arrivals and departures while using the most efficient routes. Enjoy a smooth, hassle-free journey with our modern vehicles and exceptional customer service.' },
              { code: 'MAN', name: 'Manchester Airport', desc: 'Travel to and from Manchester Airport with 24hrs-Taxicabs and enjoy a premium airport transfer service. We provide comfortable vehicles, experienced drivers, and timely pickups to ensure every journey is relaxing and efficient. Our goal is to make your airport travel convenient and worry-free.' },
              { code: 'STN', name: 'Stansted Airport', desc: '24hrs-Taxicabs provides trusted airport transfer services to Stansted Airport for individuals, families, and business travellers. Our drivers monitor traffic and flight schedules to ensure timely pickups and drop-offs. Experience reliable, safe, and comfortable transportation every time you travel.' },
              { code: 'LTN', name: 'Luton Airport', desc: '24hrs-Taxicabs offers professional taxi services to and from Luton Airport with affordable pricing and exceptional customer care. Our clean vehicles and experienced drivers ensure every journey is smooth, comfortable, and punctual. Count on us for stress-free airport transportation at any time.' },
              { code: 'BHX', name: 'Birmingham Airport', desc: '24hrs-Taxicabs provides reliable airport transfers to Birmingham Airport, delivering comfort, convenience, and punctual service. Our professional drivers ensure every passenger enjoys a safe and pleasant journey. Whether travelling for work or holidays, we\'re here to get you there on time.' },
              { code: 'EDI', name: 'Edinburgh Airport', desc: '24hrs-Taxicabs offers dependable airport transfers to Edinburgh Airport with professional drivers and comfortable vehicles. We focus on punctuality, safety, and customer satisfaction to ensure every trip is enjoyable. Book with confidence and travel without the stress of airport parking or public transport.' },
              { code: 'GLA', name: 'Glasgow Airport', desc: '24hrs-Taxicabs provides efficient airport transportation to Glasgow Airport, offering reliable service for every type of traveller. Our experienced drivers ensure timely pickups and comfortable journeys using clean, modern vehicles. Enjoy a smooth airport transfer with a company you can trust.' },
              { code: 'BRS', name: 'Bristol Airport', desc: '24hrs-Taxicabs offers comfortable and reliable airport transfers to Bristol Airport with professional service you can depend on. We provide punctual pickups, spacious vehicles, and experienced drivers to make every journey relaxing. Travel confidently knowing you\'re in safe hands.' },
              { code: 'NCL', name: 'Newcastle Airport', desc: '24hrs-Taxicabs delivers reliable transfers to Newcastle Airport with a commitment to safety, punctuality, and customer satisfaction. Our drivers are experienced and dedicated to providing a smooth travel experience from start to finish. Choose 24hrs-Taxicabs for convenient airport transportation.' },
              { code: 'LPL', name: 'Liverpool John Lennon Airport', desc: '24hrs-Taxicabs provides premium airport transfer services to Liverpool John Lennon Airport for business and leisure travellers alike. We ensure timely pickups, comfortable rides, and professional customer service throughout your journey. Let us make your airport travel simple and stress-free.' },
              { code: 'EMA', name: 'East Midlands Airport', desc: '24hrs-Taxicabs offers dependable airport taxi services to East Midlands Airport with a focus on reliability and comfort. Our experienced drivers and well-maintained vehicles ensure a safe and enjoyable journey. We strive to provide a hassle-free airport transfer every time.' },
              { code: 'ABZ', name: 'Aberdeen Airport', desc: '24hrs-Taxicabs provides long-distance airport transfers to Aberdeen Airport with professional drivers and spacious, comfortable vehicles. We ensure every journey is safe, relaxing, and completed on schedule. Enjoy dependable transportation with exceptional customer service.' },
              { code: 'BFS', name: 'Belfast International Airport', desc: '24hrs-Taxicabs offers efficient airport transfer services to Belfast International Airport, making every trip convenient and comfortable. Our team focuses on punctuality, customer care, and reliable transportation for every passenger. Trust 24hrs-Taxicabs for a smooth airport travel experience.' },
              { code: 'LCY', name: 'London City Airport', desc: '24hrs-Taxicabs provides fast and reliable transfers to London City Airport, perfect for business and leisure travellers. Our professional drivers ensure timely arrivals while delivering exceptional comfort and customer service. Travel with confidence and let 24hrs-Taxicabs handle your airport journey from start to finish.' },
            ].map((a) => (
              <div className="tc-airports-list__item" key={a.code}>
                <div className="tc-airports-list__header">
                  <div className="tc-airports-list__icon"><i className="fas fa-plane-departure"></i></div>
                  <div>
                    <span className="tc-airports-list__code">{a.code}</span>
                    <h4 className="tc-airports-list__name">{a.name}</h4>
                  </div>
                </div>
                <p className="tc-airports-list__desc">{a.desc}</p>
                <a href="https://booktaxinow.co.uk/" target="_blank" rel="noreferrer" className="tc-airports-list__btn">
                  <i className="fas fa-calendar-check"></i> Book Now
                </a>
              </div>
            ))}
          </div>
          <div className="tc-airport__cta">
            <a href="tel:02038393300" className="tc-btn tc-btn--primary"><i className="fas fa-phone"></i> Call to Book</a>
            <a href="https://booktaxinow.co.uk/" target="_blank" rel="noreferrer" className="tc-btn tc-btn--outline-dark"><i className="fas fa-calendar-check"></i> Book Online</a>
          </div>
        </div>
      </section>

      {/* â”€â”€ ABOUT â”€â”€ */}
      <section className="tc-section tc-section--grey" id="about">
        <div className="tc-container tc-about">
          <div className="tc-about__text">
            <p className="tc-section__eyebrow">Who We Are</p>
            <h2 className="tc-section__title">Trusted Taxi Service<br /><span className="tc-yellow">Since 2014</span></h2>
            <p>Taxicabs 24hrs was founded with one mission: to provide every passenger with a safe, comfortable and reliable ride â€” no matter the hour. Our fleet of professional drivers are background-checked, licensed and trained to the highest standards.</p>
            <p>We believe everyone deserves quality transportation. That's why we offer transparent pricing, no hidden fees, and a 24/7 customer support line to ensure your journey is seamless from start to finish.</p>
            <div className="tc-about__list">
              <div className="tc-about__item"><i className="fas fa-check-circle"></i><span>Licensed &amp; Fully Insured</span></div>
              <div className="tc-about__item"><i className="fas fa-check-circle"></i><span>Background-Checked Drivers</span></div>
              <div className="tc-about__item"><i className="fas fa-check-circle"></i><span>GPS Tracked Vehicles</span></div>
              <div className="tc-about__item"><i className="fas fa-check-circle"></i><span>No Surge Pricing Ever</span></div>
            </div>
          </div>
          <div className="tc-about__visual">
            <img
              src={`${process.env.PUBLIC_URL}/images/Minicabs.webp`}
              alt="About 24hrs-Taxicabs â€” Professional taxi service"
              className="tc-about__img"
            />
          </div>
        </div>
      </section>

      {/* â”€â”€ TESTIMONIALS â”€â”€ */}
      <section className="tc-section tc-section--dark" id="testimonials">
        <div className="tc-container">
          <div className="tc-section__header">
            <p className="tc-section__eyebrow tc-yellow">Customer Reviews</p>
            <h2 className="tc-section__title">What Our Riders <span className="tc-yellow">Say</span></h2>
          </div>
          <div className="tc-testimonials">
            {[
              { name: 'Sarah Mitchell', role: 'Business Traveler', rating: 5, text: 'Absolutely fantastic service. Driver was on time, professional and the car was spotless. Will definitely use Taxicabs 24hrs again!' },
              { name: 'James O\'Brien', role: 'Regular Customer', rating: 5, text: 'I\'ve been using Taxicabs 24hrs for two years. Reliable every single time. Best taxi service in the city, hands down.' },
              { name: 'Amelia Chen', role: 'Airport Traveler', rating: 5, text: 'They tracked my delayed flight and still had a driver waiting. Incredible service. Stress-free airport experience!' },
            ].map((t, i) => (
              <div className="tc-testimonial" key={i}>
                <div className="tc-testimonial__stars">{'â˜…'.repeat(t.rating)}</div>
                <p className="tc-testimonial__text">"{t.text}"</p>
                <div className="tc-testimonial__author">
                  <div className="tc-testimonial__avatar"><i className="fas fa-user"></i></div>
                  <div>
                    <strong>{t.name}</strong>
                    <span>{t.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* â”€â”€ CONTACT â”€â”€ */}
      <section className="tc-section tc-section--grey" id="contact">
        <div className="tc-container">
          <div className="tc-section__header">
            <p className="tc-section__eyebrow">Get In Touch</p>
            <h2 className="tc-section__title">Contact <span className="tc-yellow">Us</span></h2>
          </div>
          <div className="tc-contact">
            <div className="tc-contact__item">
              <div className="tc-contact__icon"><i className="fas fa-phone"></i></div>
              <div>
                <h4>Call Us 24/7</h4>
                <p><a href="tel:02038393300" style={{color:'inherit',textDecoration:'none'}}>020 3839 3300</a></p>
              </div>
            </div>
            <div className="tc-contact__item">
              <div className="tc-contact__icon"><i className="fas fa-envelope"></i></div>
              <div>
                <h4>Email Us</h4>
                <p><a href="mailto:info@24hrs-taxicabs.co.uk" style={{color:'inherit',textDecoration:'none'}}>info@24hrs-taxicabs.co.uk</a></p>
              </div>
            </div>
            <div className="tc-contact__item">
              <div className="tc-contact__icon"><i className="fas fa-map-marker-alt"></i></div>
              <div>
                <p>Taxicabs 24hrs</p>
                <p>London, United Kingdom</p>
              </div>
            </div>
            <div className="tc-contact__item">
              <div className="tc-contact__icon"><i className="fab fa-whatsapp"></i></div>
              <div>
                <h4>WhatsApp</h4>
                <p><a href="https://wa.me/447365874733" target="_blank" rel="noreferrer" style={{color:'inherit',textDecoration:'none'}}>07365 874 733</a></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* â”€â”€ CONTACT BANNER â”€â”€ */}
      <div className="tc-contact-banner">
        <img
          src={`${process.env.PUBLIC_URL}/images/Airport Transfersslider.jpg`}
          alt="24hrs-Taxicabs Book Your Ride"
          className="tc-contact-banner__img"
        />
        <div className="tc-contact-banner__overlay">
          <div className="tc-contact-banner__content">
            <p className="tc-contact-banner__eyebrow"><i className="fas fa-taxi"></i> &nbsp;Available 24 / 7</p>
            <h2 className="tc-contact-banner__title">Ready to Book Your <span>Next Ride?</span></h2>
            <p className="tc-contact-banner__sub">Professional drivers, fixed prices, no hidden fees â€” anytime, anywhere in the UK.</p>
            <div className="tc-contact-banner__btns">
              <a href="https://booktaxinow.co.uk/" target="_blank" rel="noreferrer" className="tc-contact-banner__btn tc-contact-banner__btn--primary">
                <i className="fas fa-calendar-check"></i> Book Now
              </a>
              <a href="tel:02038393300" className="tc-contact-banner__btn tc-contact-banner__btn--outline">
                <i className="fas fa-phone"></i> 020 3839 3300
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* â”€â”€ FOOTER â”€â”€ */}
      <footer className="tc-footer">
        <div className="tc-container tc-footer__inner">
          <div className="tc-footer__brand">
            <div className="tc-navbar__brand" style={{ marginBottom: '14px' }}>
              <span className="tc-navbar__icon">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <text x="14" y="13" textAnchor="middle" fill="white" fontSize="11" fontWeight="900" fontFamily="Arial,sans-serif">24</text>
                  <text x="14" y="23" textAnchor="middle" fill="rgba(255,255,255,0.82)" fontSize="6.5" fontWeight="700" fontFamily="Arial,sans-serif" letterSpacing="1.5">HRS</text>
                </svg>
              </span>
              <span className="tc-navbar__name">24hrs-<span className="tc-yellow">Taxicabs</span></span>
            </div>
            <p>Your trusted premium ride partner â€” available around the clock, every day of the year.</p>
          </div>
          <div className="tc-footer__links">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="#home">Home</a></li>
              <li><a href="#services">Services</a></li>
              <li><a href="#about">About Us</a></li>
              <li><a href="#airport">Airport Transfers</a></li>
            </ul>
          </div>
          <div className="tc-footer__links">
            <h4>Services</h4>
            <ul>
              <li><a href="#services">City Rides</a></li>
              <li><a href="#services">Corporate Travel</a></li>
              <li><a href="#services">Long Distance</a></li>
              <li><a href="#services">Medical Transport</a></li>
            </ul>
          </div>
          <div className="tc-footer__links">
            <h4>Contact</h4>
            <ul>
              <li><i className="fas fa-phone"></i> <a href="tel:02038393300" style={{color:'inherit',textDecoration:'none'}}>020 3839 3300</a></li>
              <li><i className="fab fa-whatsapp"></i> <a href="https://wa.me/447365874733" target="_blank" rel="noreferrer" style={{color:'inherit',textDecoration:'none'}}>07365 874 733</a></li>
              <li><i className="fas fa-envelope"></i> <a href="mailto:info@24hrs-taxicabs.co.uk" style={{color:'inherit',textDecoration:'none'}}>info@24hrs-taxicabs.co.uk</a></li>
              <li><i className="fas fa-map-marker-alt"></i> London, United Kingdom</li>
              <li><i className="fas fa-clock"></i> Open 24 Hours / 7 Days</li>
            </ul>
          </div>
        </div>
        <div className="tc-footer__bottom">
          <p>&copy; 2026 24hrs-Taxicabs. All rights reserved.</p>
        </div>
      </footer>

    </div>
      } />
    </Routes>
    {/* â”€â”€ WHATSAPP FLOAT â”€â”€ */}
    <a
      href="https://wa.me/447365874733"
      target="_blank"
      rel="noreferrer"
      className="tc-whatsapp-float"
      aria-label="Chat on WhatsApp"
    >
      <i className="fab fa-whatsapp"></i>
    </a>
    </>
  );
}

export default App;

